import Project from '@/models/project'
import type { ProjectJson, ProjectListResponse } from '@ironsight/types'
import { useQuery, UseQueryReturnType } from '@tanstack/vue-query'
import { MaybeRef } from '@vueuse/core'
import { AxiosError, AxiosResponse } from 'axios'
import { computed, unref, UnwrapNestedRefs } from 'vue'
import { ActionResult, getProject, getProjects } from './projects'

export const ProjectsQueryConfig = {
  queryKey: 'projects',
  details: {
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  },
  list: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  },
}

export interface SearchProjectOptions {
  offset: number
  limit: number
  query?: string
}

/**
 * Get Project Details.
 */
export const useGetProjectQuery = (
  projectId: MaybeRef<string>,
  options?: { enabled: MaybeRef<UnwrapNestedRefs<boolean>> },
): UseQueryReturnType<Project, null> => {
  const enabled = unref(options?.enabled) ?? true

  return useQuery({
    queryKey: computed(() => [ProjectsQueryConfig.queryKey, 'details', unref(projectId)]),
    queryFn: async () => {
      const response: AxiosResponse<ProjectJson> = await getProject(unref(projectId))
      return new Project(response.data)
    },
    retry: false,
    keepPreviousData: true,
    enabled,
    staleTime: ProjectsQueryConfig.details.staleTime,
    cacheTime: ProjectsQueryConfig.details.gcTime,
    refetchInterval: false,
  })
}

/**
 * Get Paginated List of Projects.
 */
export const useGetProjectsQuery = (
  query: MaybeRef<UnwrapNestedRefs<SearchProjectOptions>>,
): UseQueryReturnType<ProjectListResponse, AxiosError> => {
  return useQuery({
    queryKey: computed(() => [ProjectsQueryConfig.queryKey, 'list', query]),
    queryFn: async () => {
      const response = await getProjects(unref(query))
      return {
        data: response.data.data.map((projectJson: ProjectJson) => new Project(projectJson)),
        meta: response.data.meta,
      }
    },
    retry: false,
    keepPreviousData: true,
    staleTime: ProjectsQueryConfig.list.staleTime,
    cacheTime: ProjectsQueryConfig.list.gcTime,
    refetchInterval: false,
  })
}
